import React, { useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { products } from '../data/products';
import { ProductCard } from '../components/products/ProductCard';
import { ProductComparison } from '../components/products/ProductComparison';
import { Button } from '../components/ui/Button';
import { useTranslation } from '../lib/i18n';
interface ProductsPageProps {
  onRequestQuote: () => void;
}
export function ProductsPage({
  onRequestQuote
}: ProductsPageProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const handleScroll = () => {
    if (!scrollRef.current || !scrollRef.current.children[0]) return;
    const scrollLeft = scrollRef.current.scrollLeft;
    const itemWidth = scrollRef.current.children[0].clientWidth;
    // Calculate which item is currently most visible
    const newIndex = Math.round(scrollLeft / itemWidth);
    setActiveIndex(Math.min(newIndex, products.length - 1));
  };
  const scrollTo = (index: number) => {
    if (!scrollRef.current || !scrollRef.current.children[0]) return;
    const itemWidth = scrollRef.current.children[0].clientWidth;
    scrollRef.current.scrollTo({
      left: index * itemWidth,
      behavior: 'smooth'
    });
  };
  const prev = () => {
    if (activeIndex === 0) {
      // Loop to end
      scrollTo(products.length - 1);
    } else {
      scrollTo(activeIndex - 1);
    }
  };
  const next = () => {
    if (!scrollRef.current) return;
    // Check if we are at the end of the scrollable area
    const isAtEnd = Math.ceil(scrollRef.current.scrollLeft + scrollRef.current.clientWidth) >= scrollRef.current.scrollWidth;
    if (isAtEnd || activeIndex === products.length - 1) {
      // Loop to start
      scrollTo(0);
    } else {
      scrollTo(activeIndex + 1);
    }
  };
  return <main className="min-h-screen bg-dark-bg" data-id="element-960">
      {/* Hero */}
      <section className="py-16 md:py-20 bg-dark-surface border-b-2 border-dark-border" data-id="element-961">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-962">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6" data-id="element-963">
            D-TECH{' '}
            <span className="text-brand-yellow" data-id="element-964">
              {en ? 'products' : ru ? 'продукция' : 'proizvodi'}
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-965">
            {lang === 'en' ? 'SCA and SCB series of professional stair climbers.' : lang === 'ru' ? 'Серия профессиональных лестничных подъемников SCA и SCB.' : 'SCA i SCB serija profesionalnih stepenišnih transportera.'}
          </p>
        </div>
      </section>

      {/* Product Carousel */}
      <section className="py-14 md:py-16 border-b-2 border-dark-border" data-id="element-966">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-967">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3 text-center" data-id="element-968">
            {en ? 'Choose a ' : ru ? 'Выберите ' : 'Izaberite '}
            <span className="text-brand-yellow" data-id="element-969">
              {en ? 'model' : ru ? 'модель' : 'model'}
            </span>
          </h2>
          <p className="text-gray-400 text-center mb-10 max-w-2xl mx-auto" data-id="element-970">
            {en ? 'SCA and SCB series of professional stair climbers.' : ru ? 'Серия профессиональных лестничных подъемников SCA и SCB.' : 'SCA i SCB serija profesionalnih stepenišnih transportera.'}
          </p>

          {/* Carousel wrapper */}
          <div className="relative group" data-id="element-971">
            {/* Left arrow */}
            <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface border border-dark-border text-gray-400 hover:text-white hover:border-brand-yellow transition-colors shadow-lg opacity-0 group-hover:opacity-100 md:opacity-100 focus:opacity-100" aria-label="Prethodni" data-id="element-972">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-973" />
            </button>

            {/* Sliding track */}
            <div ref={scrollRef} onScroll={handleScroll} className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-974">
              {products.map(product => <div key={product.id} className="flex-shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] snap-center sm:snap-start" data-id="element-975">
                  <ProductCard product={product} layout="vertical" onRequestQuote={onRequestQuote} data-id="element-976" />
                </div>)}
            </div>

            {/* Right arrow */}
            <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-dark-surface border border-dark-border text-gray-400 hover:text-white hover:border-brand-yellow transition-colors shadow-lg opacity-0 group-hover:opacity-100 md:opacity-100 focus:opacity-100" aria-label="Sledeći" data-id="element-977">
              <ChevronRightIcon className="w-5 h-5" data-id="element-978" />
            </button>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-4" data-id="element-979">
            {products.map((_, idx) => <button key={idx} onClick={() => scrollTo(idx)} className={`transition-all rounded-full ${idx === activeIndex ? 'w-6 h-1.5 bg-brand-yellow' : 'w-1.5 h-1.5 bg-gray-700 hover:bg-gray-500'}`} aria-label={`Strana ${idx + 1}`} data-id="element-980" />)}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-14 md:py-16 border-b-2 border-dark-border bg-dark-surface" data-id="element-981">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-982">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-3 text-center" data-id="element-983">
            {en ? 'Compare ' : ru ? 'Сравнить ' : 'Uporedi '}
            <span className="text-brand-yellow" data-id="element-984">
              {en ? 'all models' : ru ? 'все модели' : 'sve modele'}
            </span>
          </h2>
          <p className="text-gray-400 text-center mb-10 max-w-2xl mx-auto" data-id="element-985">
            {en ? 'Complete range of D-TECH configurations.' : ru ? 'Полный ассортимент конфигураций D-TECH.' : 'Kompletna ponuda D-TECH konfiguracija.'}
          </p>
          <ProductComparison data-id="element-986" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-16 bg-dark-surface" data-id="element-987">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-988">
          <h2 className="text-3xl font-black text-white mb-4" data-id="element-989">
            {en ? 'Not sure which model?' : ru ? 'Не уверены, какую модель выбрать?' : 'Niste sigurni koji model?'}
          </h2>
          <p className="text-gray-400 mb-8" data-id="element-990">
            {en ? 'Contact us, we will help you choose the right solution for your working conditions.' : ru ? 'Свяжитесь с нами, мы поможем вам выбрать правильное решение для ваших условий работы.' : 'Kontaktirajte nas, pomoći ćemo vam da izaberete pravo rešenje za vaše uslove rada.'}
          </p>
          <Button variant="primary" size="lg" onClick={onRequestQuote} data-id="element-991">
            {en ? 'Request a quote' : ru ? 'Запросить цену' : 'Zatražite ponudu'}
          </Button>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-6 bg-dark-bg border-t border-dark-border" data-id="element-992">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-993">
          <p className="text-xs text-gray-500 max-w-3xl mx-auto" data-id="element-994">
            {en ? 'Product photos on the website are for informational purposes and may differ from the actual appearance of the product due to technical modifications by the supplier.' : ru ? 'Фотографии продуктов на сайте носят информационный характер и могут отличаться от реального внешнего вида продукта из-за технических модификаций поставщика.' : 'Fotografije proizvoda na sajtu su informativnog karaktera i mogu odstupati od stvarnog izgleda proizvoda usled tehničkih modifikacija dobavljača.'}
          </p>
        </div>
      </section>
    </main>;
}